import type { ReceiptSplitPlan } from "../../types/receipt";
import { useEffect, useRef, useState } from "react";
import {
  History,
  Plus,
  X,
} from "lucide-react";
import type { DashboardData, ExpenseInput, Owner } from "../../types";
import type { MooChat, MooMessage, MooAction } from "../../types/moo";
import { api } from "../../lib/api";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import MooMessages from "./MooMessages";
import MooChatList from "./MooChatList";
import MooComposer from "./MooComposer";
import { useMooSend } from "./use-moo-send";
import { useFocusTrap } from "../../hooks/use-focus-trap";

export default function Moo({
  data,
  profile,
  hidden,
  online = true,
  context,
  onChanged,
  onOpenTransaction,
}: {
  data: DashboardData;
  profile: Owner;
  hidden: boolean;
  /** Moo needs the server; grey the launcher while offline or on cached data. */
  online?: boolean;
  context: string;
  onChanged: () => void;
  onOpenTransaction: (id: string) => void;
}) {
  const [open, setOpen] = useState(false),
    [showChats, setShowChats] = useState(false),
    [chats, setChats] = useState<MooChat[]>([]),
    [chatId, setChatId] = useState(
      () =>
        localStorage.getItem(`barnyard-moo-${profile}`) || crypto.randomUUID(),
    ),
    [messages, setMessages] = useState<MooMessage[]>([]),
    [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [confirm, setConfirm] = useState<ConfirmState | null>(null),
    [editing, setEditing] = useState<Record<string, boolean>>({});
  const panel = useRef<HTMLElement>(null),
    end = useRef<HTMLDivElement>(null),
    composer = useRef<HTMLTextAreaElement>(null),
    inFlight = useRef(false);
  const listChats = async () => {
    const result = await api<{ chats: MooChat[] }>("moo/chats");
    setChats(result.chats);
    return result.chats;
  };
  const { text, setText, photos, setPhotos, previews, pendingSend, send, retry } = useMooSend({
    chatId, context, busy, setBusy, setError, setMessages, inFlight, onChanged,
    onSent: () => { setEditing({}); void listChats().catch(() => {}); },
  });
  useEffect(() => {
    if (!open || inFlight.current || pendingSend.current) return;
    let active = true;
    setBusy("Loading conversation…");
    setError("");
    void listChats()
      .then(async (rows) => {
        if (!rows.some((c) => c.id === chatId)) {
          if (active) setMessages([]);
          return;
        }
        const result = await api<{ messages: MooMessage[] }>(
          `/api/moo/messages?chatId=${chatId}`,
        );
        if (active) setMessages(result.messages);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setBusy("");
      });
    return () => {
      active = false;
    };
  }, [open, chatId, pendingSend]);
  useEffect(() => {
    localStorage.setItem(`barnyard-moo-${profile}`, chatId);
  }, [chatId, profile]);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, busy]);
  useFocusTrap(panel, () => setOpen(false), open && !hidden);
  async function revise(m: MooMessage, index: number, expense?: ExpenseInput, item?: MooAction["item"], remove?: boolean, receiptSplit?: ReceiptSplitPlan, repairLinePrices?: boolean) {
    if (inFlight.current) throw new Error("Wait for the current request.");
    inFlight.current = true;
    setBusy("Updating preview…");
    try {
      const result = await api<{ messages: MooMessage[] }>("moo/revise", {
        chatId,
        messageId: m.id,
        revision: m.payload.revision,
        index,
        expense, item, remove, receiptSplit, repairLinePrices,
      });
      setMessages(result.messages);
    } finally {
      setBusy("");
      inFlight.current = false;
    }
  }
  async function save(m: MooMessage) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy("Saving…");
    setError("");
    try {
      const result = await api<{ messages: MooMessage[] }>("moo/confirm", {
        chatId,
        messageId: m.id,
        revision: m.payload.revision,
      });
      setMessages(result.messages);
      onChanged();
    } catch (e) {
      setError(
        (e as Error).message +
          " Reopen this chat to check progress before retrying.",
      );
    } finally {
      setBusy("");
      inFlight.current = false;
    }
  }
  function newChat() {
    setChatId(crypto.randomUUID());
    setMessages([]);
    setShowChats(false);
    setText("");
    setPhotos([]);
    setEditing({});
  }
  const openTransaction = (id: string) => {
    setOpen(false);
    onOpenTransaction(id);
  };
  useEffect(() => {
    if (!online && open) setOpen(false);
  }, [online, open]);
  if (hidden) return null;
  const messagesProps = {
    messages, setMessages, data, profile, busy, setError, editing, setEditing, previews,
    setText, revise, save, openTransaction, setConfirm, composer, end, chatId, onChanged,
  };
  return (
    <>
      {!open && (
        <button
          className="moo-launcher"
          aria-label={online ? "Open Moo assistant" : "Moo unavailable offline"}
          title={online ? undefined : "Available when online"}
          disabled={!online}
          onClick={() => setOpen(true)}
        >
          <img
            className="moo-icon"
            src="/moo.svg"
            alt=""
            width="32"
            height="32"
          />
        </button>
      )}
      {open && online && (
        <div className="moo-backdrop" onMouseDown={() => setOpen(false)}>
          <section
            className="moo-panel"
            ref={panel}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Moo assistant"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header className="moo-header">
              <img
                className="moo-avatar"
                src="/moo.svg"
                alt=""
                width="38"
                height="38"
              />
              <div>
                <strong>Moo</strong>
                <small>
                  {profile}’s assistant
                  {data.environment === "staging" ? " · Test data" : ""}
                </small>
              </div>
              <button
                disabled={!!busy}
                aria-label="Conversations"
                onClick={() => setShowChats((v) => !v)}
              >
                <History size={20} />
              </button>
              <button disabled={!!busy} aria-label="New chat" onClick={newChat}>
                <Plus size={21} />
              </button>
              <button aria-label="Close Moo" onClick={() => setOpen(false)}>
                <X size={22} />
              </button>
            </header>
            {showChats ? (
              <MooChatList
                chats={chats}
                onOpen={(c) => {
                  setChatId(c.id);
                  setShowChats(false);
                  setEditing({});
                }}
                onDelete={(c) =>
                  setConfirm({
                    title: "Delete this chat?",
                    message:
                      "This removes its messages and drafts. Saved expenses and prices remain.",
                    danger: true,
                    confirmLabel: "Delete chat",
                    onConfirm: async () => {
                      await api("moo/delete", { chatId: c.id });
                      await listChats();
                      if (chatId === c.id) newChat();
                    },
                  })
                }
              />
            ) : (
              <>
                <MooMessages {...messagesProps} />
                <MooComposer
                  text={text}
                  setText={setText}
                  photos={photos}
                  setPhotos={setPhotos}
                  busy={busy}
                  error={error}
                  setError={setError}
                  composer={composer}
                  sending={() => inFlight.current}
                  onSend={() => void send()}
                  onRetry={retry}
                  onDraftChange={() => setError("")}
                />
              </>
            )}
          </section>
        </div>
      )}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </>
  );
}

